// Парчетата на потвърждаващия диалог: прегледът на последствията, бележките,
// изборите-карти и футърът (подсказка за клавишите + бутоните). Сглобява ги
// `ConfirmPanel`; тук е само видът.
import type { ReactNode } from "react";
import { InfoCircleOutlined, LoadingOutlined, LockOutlined, UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { KbdChip } from "../KbdChip";
import {
  DANGER_BUTTON_CLASS,
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
} from "../list/buttonStyles";
import type { ConfirmChoice, ConfirmNote, ConfirmPreview } from "./confirmTypes";
import { ACTION_ATTR } from "./useConfirmKeyboard";

const DEFAULT_PREVIEW_LIMIT = 6;
const NOTE_ICON: Record<ConfirmNote["kind"], ReactNode> = {
  undo: <UndoOutlined />,
  final: <LockOutlined />,
  info: <InfoCircleOutlined />,
};

// Фокусът на бутоните в диалога се вижда и след клик с мишката (стартовият
// фокус е програмен — без пръстен не личи кой бутон ще натисне Enter)
export const FOCUS_RING = "focus:shadow-[0_0_0_3px_var(--color-brand-tint)]";
export const DANGER_FOCUS_RING = "focus:shadow-[0_0_0_3px_var(--color-danger-tint2)]";

export const PreviewList = ({ preview }: { preview: ConfirmPreview }) => {
  const { t } = useTranslation();
  const limit = preview.limit ?? DEFAULT_PREVIEW_LIMIT;
  const shown = preview.items.slice(0, limit);
  const total = Math.max(preview.total ?? 0, preview.items.length);
  const hidden = total - shown.length;
  return (
    <div className="overflow-hidden rounded-[10px] border border-shade-2">
      <div className="flex items-baseline justify-between gap-2 bg-fill px-3 py-[7px] text-[10.5px] font-bold uppercase leading-[1.2] tracking-[0.1em] text-muted">
        <span>{preview.caption}</span>
        {total > 1 && <span className="font-sheet tabular-nums">{total}</span>}
      </div>
      <ul className="m-0 max-h-[232px] list-none overflow-auto p-0">
        {shown.map((item) => (
          <li
            key={item.key}
            className="flex items-baseline justify-between gap-3 border-t border-shade-1 px-3 py-[7px] text-[13.5px] leading-[1.35]"
          >
            <span className="flex min-w-0 flex-col gap-px">
              <span className={item.tone === "muted" ? "text-muted" : "text-ink"}>{item.label}</span>
              {item.hint && (
                <span className={`text-[12px] ${item.tone === "danger" ? "text-danger-ink" : "text-muted"}`}>
                  {item.hint}
                </span>
              )}
            </span>
            {item.value != null && (
              <span
                className={`shrink-0 whitespace-nowrap font-sheet font-semibold tabular-nums ${
                  item.tone === "danger" ? "text-danger-ink" : item.tone === "muted" ? "text-muted" : "text-ink"
                }`}
              >
                {item.value}
              </span>
            )}
          </li>
        ))}
        {hidden > 0 && (
          <li className="border-t border-shade-1 px-3 py-[7px] text-[12.5px] text-muted">
            {t("inventoryPage.cfmMore", { count: hidden })}
          </li>
        )}
      </ul>
    </div>
  );
};

export const NoteLine = ({ note }: { note: ConfirmNote }) => (
  <div
    className={`flex items-start gap-2 text-[12.5px] leading-[1.45] ${
      note.kind === "final" ? "text-danger-ink" : "text-body"
    }`}
  >
    <span aria-hidden className="mt-px shrink-0">
      {NOTE_ICON[note.kind]}
    </span>
    <span>{note.text}</span>
  </div>
);

export const ChoiceCards = ({ choices }: { choices: ConfirmChoice[] }) => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-2">
    {choices.map((choice) => {
      const danger = choice.tone === "danger";
      return (
        <button
          key={choice.key}
          type="button"
          {...{ [ACTION_ATTR]: `choice:${choice.key}` }}
          data-confirm-label={choice.label}
          disabled={choice.disabled}
          onClick={choice.onSelect}
          className={`flex cursor-pointer flex-col gap-[3px] rounded-[10px] border border-shade-3 bg-surface px-3.5 py-3 text-left leading-[1.35] outline-none disabled:cursor-not-allowed disabled:opacity-55 ${
            danger
              ? `hover:border-danger/45 focus:border-danger/60 ${DANGER_FOCUS_RING}`
              : `hover:border-brand/45 focus:border-brand ${FOCUS_RING}`
          }`}
        >
          <span className={`text-[14px] font-semibold ${danger ? "text-danger-ink" : "text-ink"}`}>
            {choice.label}
          </span>
          <span className="text-[12.5px] text-body">{choice.hint}</span>
        </button>
      );
    })}
  </div>
);

// Подсказката вляво във футъра (само десктоп): какво прави Enter СЕГА (бутона
// на фокус; от поле за писане — Ctrl+Enter потвърждава) и какво прави Esc
const KeyHint = ({
  enterLabel,
  fieldConfirmLabel,
  escLabel,
}: {
  enterLabel: string | null;
  fieldConfirmLabel: string | null;
  escLabel: string;
}) => (
  <span className="mr-auto flex min-w-0 items-center gap-1.5 text-[11.5px] text-muted">
    {fieldConfirmLabel ? (
      <>
        <KbdChip>Ctrl+Enter</KbdChip>
        <span className="truncate">{fieldConfirmLabel}</span>
        <span aria-hidden>·</span>
      </>
    ) : enterLabel && enterLabel !== escLabel ? (
      <>
        <KbdChip>Enter</KbdChip>
        <span className="truncate">{enterLabel}</span>
        <span aria-hidden>·</span>
      </>
    ) : (
      enterLabel && <KbdChip>Enter</KbdChip>
    )}
    <KbdChip>Esc</KbdChip>
    <span className="truncate">{escLabel}</span>
  </span>
);

type FooterProps = {
  isMobile: boolean;
  isDesktop: boolean;
  padX: string;
  danger: boolean;
  busy: boolean;
  cancelText: string;
  hideCancel: boolean;
  confirmLabel?: string;
  confirmDisabled: boolean;
  hint: { enterLabel: string | null; fieldConfirmLabel: string | null };
  onCancel: () => void;
  onConfirm?: () => void;
};

export const ConfirmFooter = ({
  isMobile,
  isDesktop,
  padX,
  danger,
  busy,
  cancelText,
  hideCancel,
  confirmLabel,
  confirmDisabled,
  hint,
  onCancel,
  onConfirm,
}: FooterProps) => {
  // Футър без бутони (само избори-карти) се рисува само заради подсказката
  if (!isDesktop && hideCancel && !confirmLabel) return null;
  return (
    <div
      className={`flex gap-2 border-t border-shade-2 bg-panel ${padX} py-3 ${
        isMobile ? "flex-col-reverse items-stretch" : "items-center"
      }`}
    >
      {isDesktop ? (
        <KeyHint {...hint} escLabel={cancelText} />
      ) : (
        !isMobile && <div className="flex-1" />
      )}
      {!hideCancel && (
        <button
          type="button"
          {...{ [ACTION_ATTR]: "cancel" }}
          data-confirm-label={cancelText}
          onClick={onCancel}
          disabled={busy}
          className={`${SECONDARY_BUTTON_CLASS} !py-2.5 ${FOCUS_RING}`}
        >
          {cancelText}
        </button>
      )}
      {confirmLabel && (
        <button
          type="button"
          {...{ [ACTION_ATTR]: "confirm" }}
          data-confirm-label={confirmLabel}
          onClick={onConfirm}
          disabled={busy || confirmDisabled}
          className={`${danger ? DANGER_BUTTON_CLASS : PRIMARY_BUTTON_CLASS} ${
            danger ? DANGER_FOCUS_RING : FOCUS_RING
          }`}
        >
          {busy && <LoadingOutlined />}
          {confirmLabel}
        </button>
      )}
    </div>
  );
};
